import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import Iconify from 'src/components/iconify';
import Label from 'src/components/label';
import { BulkDeleteAffectedBenefit } from 'src/types/products';
import type { ThemesCategoriesTab } from '../themes-categories-list.hook';

type DeletionImpactDialogProps = {
  open: boolean;
  entity: ThemesCategoriesTab;
  affectedBenefitsCount: number;
  affectedEntityNames: string[];
  benefits: BulkDeleteAffectedBenefit[];
  isDeleting: boolean;
  onClose: VoidFunction;
  onConfirm: VoidFunction;
};

const formatDate = (value: string) => {
  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('pt-BR');
};

export default function DeletionImpactDialog({
  open,
  entity,
  affectedBenefitsCount,
  affectedEntityNames,
  benefits,
  isDeleting,
  onClose,
  onConfirm,
}: DeletionImpactDialogProps) {
  const isThemes = entity === 'themes';
  const entityLabel = isThemes ? 'Temas impactados' : 'Categorias impactadas';
  const benefitLabel =
    affectedBenefitsCount === 1 ? 'benefício será impactado' : 'benefícios serão impactados';

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={isDeleting ? undefined : onClose}
      disableEscapeKeyDown={isDeleting}
    >
      <DialogTitle sx={{ pb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              width: 34,
              height: 34,
              flexShrink: 0,
              display: 'grid',
              placeItems: 'center',
              borderRadius: 1,
              bgcolor: 'warning.lighter',
              color: 'warning.dark',
            }}
          >
            <Iconify icon="mdi:alert-outline" sx={{ width: 21, height: 21 }} />
          </Box>
          <Typography variant="h6">Esta exclusão impactará benefícios ativos</Typography>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          {affectedBenefitsCount} {benefitLabel}. Confira uma prévia antes de continuar.
        </Typography>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {entityLabel}:
        </Typography>
        <Stack direction="row" flexWrap="wrap" useFlexGap gap={1} sx={{ mb: 2.5 }}>
          {affectedEntityNames.map((name, index) => (
            <Chip key={`${name}-${index}`} label={name} size="small" variant="outlined" />
          ))}
        </Stack>

        <Typography variant="subtitle2" sx={{ mb: 1.25 }}>
          Prévia dos benefícios impactados:
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: `repeat(${Math.max(Math.min(benefits.length, 3), 1)}, minmax(0, 1fr))`,
            },
            gap: 1.5,
          }}
        >
          {benefits.slice(0, 3).map((benefit) => {
            const isProduct = benefit.type === 'product';

            return (
              <Card
                key={`${benefit.type}-${benefit.id}`}
                variant="outlined"
                sx={{
                  height: '100%',
                  display: 'flex',
                  overflow: 'hidden',
                  flexDirection: 'column',
                  boxShadow: 0,
                }}
              >
                <Box
                  sx={{
                    width: 1,
                    height: 128,
                    flexShrink: 0,
                    display: 'grid',
                    placeItems: 'center',
                    overflow: 'hidden',
                    bgcolor: 'grey.100',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  {benefit.image ? (
                    <Box
                      component="img"
                      src={benefit.image}
                      alt={benefit.name}
                      sx={{ width: 1, height: 1, display: 'block', objectFit: 'cover' }}
                    />
                  ) : (
                    <Stack spacing={0.5} alignItems="center" color="text.disabled">
                      <Iconify icon="mdi:image-off-outline" sx={{ width: 30, height: 30 }} />
                      <Typography variant="caption" color="inherit">
                        Sem imagem
                      </Typography>
                    </Stack>
                  )}
                </Box>

                <Stack
                  spacing={1.5}
                  p={2}
                  sx={{
                    position: 'relative',
                    zIndex: 1,
                    flex: 1,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      alignSelf: 'flex-start',
                      gap: 1,
                      px: 1.6,
                      py: 0.6,
                      borderRadius: 999,
                      border: '1px solid',
                      borderColor: 'grey.300',
                      backgroundColor: 'grey.100',
                      color: 'text.primary',
                      fontWeight: 600,
                      minWidth: 110,
                    }}
                  >
                    {isProduct ? (
                      <Iconify icon="mdi:cube-outline" sx={{ width: 18, height: 18 }} />
                    ) : (
                      <ConfirmationNumberOutlinedIcon sx={{ fontSize: 18 }} />
                    )}
                    <Box component="span">{isProduct ? 'Produto' : 'Cupom'}</Box>
                  </Box>

                  <Typography
                    variant="subtitle2"
                    title={benefit.name}
                    sx={{
                      overflow: 'hidden',
                      display: '-webkit-box',
                      wordBreak: 'break-word',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: 2,
                    }}
                  >
                    {benefit.name}
                  </Typography>

                  <Box sx={{ mt: 'auto' }}>
                    {!benefit.startDate && !benefit.endDate ? (
                      <Label
                        variant="soft"
                        color="default"
                        sx={{
                          border: '1px solid',
                          borderColor: 'grey.300',
                          color: 'text.secondary',
                          fontWeight: 700,
                        }}
                      >
                        Sem data
                      </Label>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Vigência: {benefit.startDate ? formatDate(benefit.startDate) : '-'} a{' '}
                        {benefit.endDate ? formatDate(benefit.endDate) : '-'}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </Card>
            );
          })}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={isDeleting}>
          Cancelar
        </Button>
        <LoadingButton variant="contained" color="error" onClick={onConfirm} loading={isDeleting}>
          Excluir mesmo assim
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
